import React from "react";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import PropTypes from "prop-types";
import {imageUpload} from "./actions/imageAction";
import {changeCheckbox, getSeniors} from "../members/actions/seniorsAction";
import AuthLayout from "../../shared/containers/AuthLayout";
import * as notificationAction
  from "../../shared/modules/notification/actions/notificationsAction";
import {InputFieldGroup} from "../../shared/components/InputFieldGroup";
import {MAX_IMAGE_TITLE_LENGTH} from "./constants";
import {MAX_UPLOAD_SIZE} from "../../shared/constants/image";
import {
  NOTIFICATION_ERROR,
  NOTIFICATION_SUCCESS,
  NOTIFICATION_WARNING
} from "../../shared/modules/notification/constants";
import Seniors from "./components/seniors";
import ImageUploader from "../../shared/components/ImageUploader";
import {ROUTER_ROOT} from "../../shared/constants/routers";
import {ANALYTICS_SPECS} from "../../shared/constants";
import {sendAnalyticsEvent} from "../../shared/actions/analytics";


class ImageUpload extends React.Component {

  static propTypes = {
    seniors: PropTypes.array,
    user: PropTypes.object
  };

  static defaultProps = {
    seniors: [],
    user: {}
  };

  state = this.getDefaultState();

  onSubmit = (evt) => {
    evt.preventDefault();
    if (this.props.seniors.filter((senior) => senior.checkbox).length) {
      const {errors, isValid} = this.validateInput(this.state);

      if (!isValid) {
        this.setState({errors});
      } else {
        this.props.history.push(ROUTER_ROOT);

        sendAnalyticsEvent(
          ANALYTICS_SPECS.DIARY.NAME,
          ANALYTICS_SPECS.DIARY.EVENTS.SELECT_CONTACT,
          {
            selected_contacts: this.props.seniors.filter((senior) => senior.checkbox).map((senior) => senior.prof_id)
          }
        );

        sendAnalyticsEvent(
          ANALYTICS_SPECS.DIARY.NAME,
          ANALYTICS_SPECS.DIARY.EVENTS.CONFIRM_SEND_PICTURE,
        );

        this.props.dispatch(imageUpload(
          this.state,
          this.props.user,
          this.props.seniors.filter((senior) => senior.checkbox)
        ))
          .then(
            () => {
              this.props.dispatch(notificationAction.addNotification(
                __("The picture was successfully uploaded."),
                NOTIFICATION_SUCCESS
              ));
            },
            (err) => {
              if (err.status === 401) {
                this.props.dispatch(notificationAction.addNotification(
                  __("Your session has expired, please register again."),
                  NOTIFICATION_WARNING
                ));
              }
              this.props.dispatch(notificationAction.addNotification(
                __("An error has occurred. Please try again later."),
                NOTIFICATION_WARNING
              ));
            }
          );
      }

      if (this.state.imageText.trim().length > 0) {
        sendAnalyticsEvent(ANALYTICS_SPECS.DIARY.NAME, ANALYTICS_SPECS.DIARY.EVENTS.IMAGE_CAPTION);
      }
    }
  };

  componentDidMount() {
    this.props.dispatch(getSeniors());
    sendAnalyticsEvent(ANALYTICS_SPECS.DIARY.NAME, ANALYTICS_SPECS.DIARY.EVENTS.SEND_PICTURE);
  }

  /**
   * Gets default state
   * @return {object} default state
   */
  getDefaultState() {
    return {

      /** Object which holds data related to image upload.
       * @typedef {object} ImageUploadState
       * @property {object} file - File to upload.
       * @property {string} imagePreviewUrl - Base64 encoded preview image.
       * @property {string} imageText - Title of the image.
       * @property {object} errors
       * @property {bool} isLoading
       */
      file: "",
      imagePreviewUrl: "",
      imageText: "",
      errors: {},
      isLoading: false
    };
  }

  /**
   * Checks image file size
   * @param {object} data - text input object
   * @param {object} state - error state object
   * @return {void}
   */
  checkImageFile(data, state) {
    if (data.file.size > MAX_UPLOAD_SIZE) {
      const max_size = Math.floor(MAX_UPLOAD_SIZE / 1024 / 1024);

      state.errors.file = __("The selected file is too big. The size of the files should be less than %max_size% MB.")
        .replace('%max_size%', max_size);
      state.isValid = false;
    } else if (data.file.size <= 0) {
      state.errors.file = __("The file was not recognized!");
      state.isValid = false;
    }
  }

  /**
   * Validates both image upload inputs (image file and text/caption)
   * @param {ImageUploadState} data - validation data
   * @return {object} state
   */
  validateInput(data) {
    const state = {
      errors: {},
      isValid: true
    };

    this.checkImageText(data, state);
    this.checkImageFile(data, state);
    return state;
  }

  /**
   * Checks image text length and char compatibility
   * @param {ImageUploadState} data - text input object
   * @param {object} state - error state object
   * @return {void}
   */
  checkImageText(data, state) {
    if (data.imageText.length > 0) {
      const delta = MAX_IMAGE_TITLE_LENGTH - this.props.user.firstname.length;

      if ((data.imageText.length) > delta) {
        state.errors.imageText = __("Title may not exceed %delta% included characters")
          .replace('%delta%', delta);
        state.isValid = false;
      } else {
        const regexChar = /^([a-zA-Z0-9äöüÄÖÜß ;:*&'"(){}/[\]@~|§\-`_°+$€=?!.,#]+)$/;

        if (!regexChar.test(data.imageText)) {
          state.isValid = false;
          state.errors.imageText = __("This title contains unauthorized special characters.");
        }
      }
    }
  }

  onChange = (evt) => {
    this.setState({[evt.target.name]: evt.target.value});
  };

  onCheckboxChange = (state, device_id, prof_id) => {
    this.props.dispatch(changeCheckbox(state, device_id, prof_id))
      .then(() => {
        if (!this.props.seniors.filter((senior) => senior.checkbox).length) {
          this.props.dispatch(
            notificationAction.addNotification(
              __("Please select at least 1 senior"),
              NOTIFICATION_ERROR
            )
          );
        }

      });
  };

  setImage = ({ file, url }) => {
    this.setState({ file, imagePreviewUrl: url });
  };

  render() {
    const {imagePreviewUrl, isLoading, imageText, errors} = this.state;
    const hasSelectedSeniors = Boolean(this.props.seniors.filter((senior) => senior.checkbox).length);
    const placeholder = __("Enter a short title here(max. %length% characters)")
      .replace('%length%', MAX_IMAGE_TITLE_LENGTH - this.props.user.firstname.length);

    return (
      <AuthLayout
        title={__("Image")}
        onBackLink={this.props.history.goBack}
        headerClass="shortHeader"
      >
        <ImageUploader imagePreviewUrl={imagePreviewUrl} onChange={this.setImage} analyticsCategory={ANALYTICS_SPECS.DIARY.NAME} />

        <div id="picture-settings" className="flex">
          <form onSubmit={this.onSubmit}>
            {imagePreviewUrl &&
            <InputFieldGroup
              name="imageText"
              type="text"
              placeholder={placeholder}
              value={imageText}
              onChange={this.onChange}
              error={errors.imageText}
              disabled={isLoading}
              id="inputField"
            />
            }
            {imagePreviewUrl && this.props.seniors.length > 1 &&
            <div className="seniorProfile">
              <Seniors
                seniors={this.props.seniors}
                onCheckbox={this.onCheckboxChange}/>
            </div>
            }
            {imagePreviewUrl && <button
              className={hasSelectedSeniors ? "buttonPrimary" : "buttonDisabled"}
              disabled={isLoading}
            >
              {__("Send a picture")}
            </button>}
          </form>
        </div>
      </AuthLayout>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    seniors: state.seniors,
    user: state.user
  };
};

export default connect(mapStateToProps)(withRouter(ImageUpload));
